import {Location} from '@reach/router'
import compact from 'lodash/compact'
import {graphql} from 'gatsby'
import {StaticResourcePageFragment} from 'gatsby-graphql-types-generated'
import React, {useEffect} from 'react'

import Breadcrumbs from 'src/components/Breadcrumbs'
import {Box, PositionContainer, Grid} from 'src/components/Box'
import {HeaderXl, Header3, Lede} from 'src/components/text'
import WysiwygContent from 'src/components/WysiwygContent'
import Layout from 'src/components/Layout'
import SEO from 'src/components/SEO'
import FlairBox from 'src/components/FlairBox'
import {FlairProvider} from 'src/context/FlairContext'
import {Maybe} from 'src/graphql-generated'
import useViewer from 'src/hooks/useViewer'
import ButtonLink from 'src/components/ButtonLink'
import {localPaths} from 'src/urls'
import {Link} from 'src/components/Link'
import HeroImage from 'src/components/HeroImage'
import useCourseCompletedInteraction from 'src/hooks/useCourseCompletedInteraction'

interface Props {
  resourcePage: StaticResourcePageFragment
}

const GatedContent: React.FunctionComponent<{
  ctaLabel?: Maybe<string>
  children?: React.ReactNode | React.ReactNode[]
}> = ({ctaLabel = 'Must be logged in to view content', children}) => {
  const {viewer} = useViewer()
  const createInteraction = useCourseCompletedInteraction()

  useEffect(() => {
    createInteraction({
      variables: {
        taskId: process.env.GATSBY_CONTENTFUL_Resume_Builder_Task_ID,
        stepId: process.env.GATSBY_CONTENTFUL_Resume_Builder_Step_ID,
      },
    })
  }, [])
  if (viewer === 'not-logged-in') {
    return (
      <Location>
        {({location}) => (
          <Grid css={{textAlign: 'center'}} justifyContent="center" gridGap="3">
            <Header3>{ctaLabel}</Header3>
            <ButtonLink
              to={localPaths.signUp}
              state={{returnTo: location.pathname}}
            >
              Sign Up
            </ButtonLink>
            <Link to={localPaths.login} state={{returnTo: location.pathname}}>
              Log in
            </Link>
          </Grid>
        )}
      </Location>
    )
  }

  if (viewer) {
    return <>{children}</>
  }

  return null
}

export const ResourcePageTemplate = ({resourcePage}: Props) => {
  const includeFlair = !resourcePage.isLegal
  // const isLms = isLmsImplementation()

  // if (isLms) {
  //   return (
  //     <>
  //       <SEO title={resourcePage.title} permalink={resourcePage.permalink} />
  //       <FlairProvider seed={parseInt(resourcePage.id, 16)}>
  //         {resourcePage.heroUrl && (
  //           <Box mx={{_: -3, lg: 0}}>
  //             <HeroImage
  //               alt={resourcePage.title}
  //               file={resourcePage.heroFile}
  //               url={resourcePage.heroUrl}
  //             />
  //           </Box>
  //         )}
  //         <Box my="2">
  //           <Breadcrumbs
  //             ancestors={resourcePage.ancestorPages.map((a) => ({
  //               name: a.title,
  //               permalink: a.permalink,
  //             }))}
  //           />
  //         </Box>
  //         <Grid
  //           as="article"
  //           mx={{default: -3, lg: 0}}
  //           px={{default: 3, lg: 0}}
  //           overflowX={{default: 'hidden', lg: 'visible'}}
  //           gridGap="4"
  //         >
  //           <PositionContainer position="relative">
  //             {includeFlair ? (
  //               <FlairBox
  //                 right={-100}
  //                 verticalCenter
  //                 width={200}
  //                 height={200}
  //               />
  //             ) : null}
  //             <HeaderXl breakWord>{resourcePage.title}</HeaderXl>
  //           </PositionContainer>

  //           {resourcePage.lede &&
  //             compact(resourcePage.lede.split(/\r?\n/)).map(
  //               (subLede, index) => <Lede key={index}>{subLede}</Lede>
  //             )}

  //           {resourcePage.body && (
  //             <WysiwygContent
  //               content={resourcePage.body}
  //               withFlair={includeFlair}
  //             />
  //           )}

  //           {resourcePage.gatedBody && (
  //             <GatedContent ctaLabel={resourcePage.ctaLabel}>
  //               <WysiwygContent
  //                 content={resourcePage.gatedBody}
  //                 withFlair={includeFlair}
  //               />
  //             </GatedContent>
  //           )}
  //         </Grid>
  //       </FlairProvider>
  //     </>
  //   )
  // }

  return (
    <Layout narrow>
      <SEO title={resourcePage.title} permalink={resourcePage.permalink} />
      <FlairProvider seed={parseInt(resourcePage.id, 16)}>
        {resourcePage.heroUrl && (
          <Box mx={{_: -3, lg: 0}}>
            <HeroImage
              alt={resourcePage.title}
              file={resourcePage.heroFile}
              url={resourcePage.heroUrl}
            />
          </Box>
        )}
        <Box my="2">
          <Breadcrumbs
            ancestors={
              resourcePage.ancestorPages !== null
                ? resourcePage.ancestorPages.map((a) => ({
                    name: a.title,
                    permalink: a.permalink,
                  }))
                : []
            }
          />
        </Box>
        <Grid
          as="article"
          mx={{default: -3, lg: 0}}
          px={{default: 3, lg: 0}}
          overflowX={{default: 'hidden', lg: 'visible'}}
          gridGap="4"
        >
          <PositionContainer position="relative">
            {includeFlair ? (
              <FlairBox right={-100} verticalCenter width={200} height={200} />
            ) : null}
            <HeaderXl breakWord>{resourcePage.title}</HeaderXl>
          </PositionContainer>

          {resourcePage.lede?.lede &&
            compact(resourcePage.lede.lede?.split(/\r?\n/)).map(
              (subLede, index) => <Lede key={index}>{subLede}</Lede>
            )}

          {resourcePage.body?.body && (
            <WysiwygContent
              content={resourcePage.body.body}
              withFlair={includeFlair}
            />
          )}

          {resourcePage.gatedBody?.gatedBody && (
            <GatedContent ctaLabel={resourcePage.ctaLabel}>
              <WysiwygContent
                content={resourcePage.gatedBody.gatedBody}
                withFlair={includeFlair}
              />
            </GatedContent>
          )}
        </Grid>
      </FlairProvider>
    </Layout>
  )
}

export const query = graphql`
  fragment StaticResourcePage on ContentfulResourcePage {
    id
    title
    permalink
    body {
      body
    }
    updatedAt
    isLegal
    gatedBody {
      gatedBody
    }
    ctaLabel
    heroUrl
    heroFile {
      ...HeroImage
    }
    lede {
      lede
    }
    ancestorPages: resource_page {
      id
      title
      permalink
    }
  }
`

export default ResourcePageTemplate
